import { useState, useCallback } from 'react';
import { uploadFile } from '../../utils/storage';
import { set } from '../../utils/objectUtils';
import { UploadStates } from '../../types/upload';

interface UploadOptions {
    fieldName: string;
    pathPrefix: string;
    urlFieldName: string;
    id?: string;
    buildPath?: (params: { file: File; id?: string; pathPrefix: string }) => string;
    onUploadComplete?: (params: { downloadURL: string; path: string; file: File }) => void;
}

// O setFormData pode ser mais genérico, mas por enquanto vamos manter
// a assinatura que já conhecemos dos nossos contextos.
type SetFormData<T> = React.Dispatch<React.SetStateAction<T>>;

export const useFileUpload = <T extends {}>(setFormData: SetFormData<T>) => {
    const [uploadStates, setUploadStates] = useState<UploadStates>({});

    const uploadWithFile = useCallback(
        async (file: File, options: UploadOptions): Promise<string | null> => {
            const { fieldName, pathPrefix, urlFieldName, id, buildPath, onUploadComplete } = options;

            // Persist the raw file while the upload runs
            setFormData(prev => set({ ...prev }, fieldName, file));

            setUploadStates(prev => ({
                ...prev,
                [fieldName]: { progress: 0, status: 'uploading', error: null, downloadURL: null }
            }));

            try {
                const path = buildPath
                    ? buildPath({ file, id, pathPrefix })
                    : id
                        ? `collaborators/${id}/${pathPrefix}/${file.name}`
                        : `${pathPrefix}/${file.name}`;

                const downloadURL = await uploadFile(file, path, (progress) => {
                    setUploadStates(prev => ({
                        ...prev,
                        [fieldName]: { ...prev[fieldName], progress, status: 'uploading' }
                    }));
                });

                setFormData(prev => {
                    let newState = set({ ...prev }, urlFieldName, downloadURL);
                    newState = set(newState, fieldName, undefined);
                    return newState;
                });

                setUploadStates(prev => ({
                    ...prev,
                    [fieldName]: { ...prev[fieldName], status: 'success', downloadURL, progress: 100, error: null }
                }));

                onUploadComplete?.({ downloadURL, path, file });

                return downloadURL;
            } catch (error: any) {
                console.error(`Upload error for ${fieldName}:`, error);
                setUploadStates(prev => ({
                    ...prev,
                    [fieldName]: { ...prev[fieldName], status: 'error', error: error.message || 'Unknown error' }
                }));
                return null;
            }
        },
        [setFormData]
    );

    const handleFileChange = useCallback(
        async (
            event: React.ChangeEvent<HTMLInputElement>,
            options: UploadOptions
        ): Promise<string | null> => {
            const file = event.target.files?.[0];
            // Allow selecting the same file twice
            event.target.value = '';

            if (!file) {
                return null;
            }

            return uploadWithFile(file, options);
        },
        [uploadWithFile]
    );

    const resetUploadState = useCallback((fieldName?: string) => {
        setUploadStates(prev => {
            if (!fieldName) {
                return {};
            }
            const { [fieldName]: _removed, ...rest } = prev;
            return rest;
        });
    }, []);

    return { uploadStates, handleFileChange, resetUploadState, uploadWithFile };
};
